import React, { useState, useEffect } from 'react'
import { CogIcon, ShieldCheckIcon, PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'

// Default risk scoring weights (comprehensive) - NORMALIZED TO SUM TO 100
const DEFAULT_WEIGHTS = {
  // Original compliance/behavioral factors (73.08 pts)
  kyc_compliance: 15.38,
  aml_screening: 15.38,
  gaming_behavior: 11.54,
  case_history: 11.54,
  smr_history: 7.69,
  ecdd_history: 3.85,
  
  // Demographic factors (15.38 pts)
  age: 3.85,
  gender: 2.31,
  nationality: 5.38,
  occupation_risk: 7.69,
  membership_duration: 3.85,
  
  // Profile factors (11.54 pts)
  confidence_level: 7.69,
  profile_age: 2.31,
  activity: 1.54
}

/** Weights persisted to venue config and used by PatronRiskCalculator (all 14 components). */
const CALCULATOR_WEIGHT_KEYS = [
  'kyc_compliance',
  'aml_screening',
  'gaming_behavior',
  'case_history',
  'smr_history',
  'ecdd_history',
  'occupation_risk',
  'age',
  'gender',
  'nationality',
  'membership_duration',
  'confidence_level',
  'profile_age',
  'activity',
]

// Demographic risk matrices
const DEFAULT_RISK_MATRICES = {
  age_groups: [
    { range: '18-24', risk_score: 15, description: 'Young adults - higher risk' },
    { range: '25-34', risk_score: 8, description: 'Lower risk group' },
    { range: '35-44', risk_score: 5, description: 'Low risk' },
    { range: '45-54', risk_score: 5, description: 'Low risk' },
    { range: '55-64', risk_score: 8, description: 'Moderate risk' },
    { range: '65+', risk_score: 15, description: 'Elderly - higher risk' }
  ],
  gender: [
    { value: 'Male', risk_score: 10, description: 'Statistically higher gaming risk' },
    { value: 'Female', risk_score: 5, description: 'Lower statistical risk' },
    { value: 'Other', risk_score: 5, description: 'Standard risk' }
  ],
  countries: [
    { country: 'Australia', risk_score: 0, risk_level: 'LOW' },
    { country: 'New Zealand', risk_score: 0, risk_level: 'LOW' },
    { country: 'United States', risk_score: 5, risk_level: 'LOW' },
    { country: 'United Kingdom', risk_score: 5, risk_level: 'LOW' },
    { country: 'China', risk_score: 15, risk_level: 'MEDIUM' },
    { country: 'Russia', risk_score: 20, risk_level: 'HIGH' },
    { country: 'Iran', risk_score: 25, risk_level: 'CRITICAL' }
  ],
  occupations: [
    { category: 'Professional Services', risk_score: 5, description: 'Lawyers, accountants, consultants' },
    { category: 'Healthcare', risk_score: 5, description: 'Doctors, nurses, medical staff' },
    { category: 'Education', risk_score: 5, description: 'Teachers, academics' },
    { category: 'Technology', risk_score: 5, description: 'IT professionals, developers' },
    { category: 'Finance', risk_score: 15, description: 'Banking, investment - higher cash access' },
    { category: 'Gaming Staff', risk_score: 20, description: 'Casino/gaming industry - insider risk' },
    { category: 'Construction', risk_score: 10, description: 'Cash-intensive industry' },
    { category: 'Retail', risk_score: 8, description: 'Cash handling' },
    { category: 'Unemployed', risk_score: 20, description: 'No verifiable income' },
    { category: 'Retired', risk_score: 10, description: 'Fixed income concerns' }
  ],
  membership_duration: [
    { range: '0-3 months', risk_score: 20, description: 'New member - unknown history' },
    { range: '3-12 months', risk_score: 15, description: 'Recent member' },
    { range: '1-3 years', risk_score: 8, description: 'Established member' },
    { range: '3-5 years', risk_score: 5, description: 'Long-term member' },
    { range: '5+ years', risk_score: 0, description: 'Very long-term member' }
  ]
}

function PatronRiskConfiguration() {
  const [weights, setWeights] = useState(DEFAULT_WEIGHTS)
  const [riskMatrices, setRiskMatrices] = useState(DEFAULT_RISK_MATRICES)
  const [showWeightConfig, setShowWeightConfig] = useState(false)
  const [showMatrixConfig, setShowMatrixConfig] = useState(null) // 'age', 'gender', 'countries', 'occupations', 'membership'
  const [tempWeights, setTempWeights] = useState(DEFAULT_WEIGHTS)
  const [tempMatrix, setTempMatrix] = useState(null)
  const [weightsSource, setWeightsSource] = useState('default')

  // Load configuration from backend
  useEffect(() => {
    loadConfiguration()
  }, [])

  const loadConfiguration = async () => {
    try {
      let merged = { ...DEFAULT_WEIGHTS }

      const res = await authFetch(getLocalApiUrl('/api/venue-config/risk-weights'))
      if (res.ok) {
        const json = await res.json()
        if (json.weights && typeof json.weights === 'object') {
          for (const key of CALCULATOR_WEIGHT_KEYS) {
            if (json.weights[key] != null && !Number.isNaN(Number(json.weights[key]))) {
              merged[key] = Number(json.weights[key])
            }
          }
          setWeightsSource('venue')
        }
      } else {
        const savedWeights = localStorage.getItem('patron_risk_weights')
        if (savedWeights) {
          merged = { ...merged, ...JSON.parse(savedWeights) }
          setWeightsSource('local')
        }
      }

      setWeights(merged)
      setTempWeights(merged)

      const savedMatrices = localStorage.getItem('patron_risk_matrices')
      if (savedMatrices) {
        const parsed = JSON.parse(savedMatrices)
        setRiskMatrices(parsed)
      }
    } catch (error) {
      console.error('Error loading configuration:', error)
    }
  }

  const handleWeightChange = (key, value) => {
    const numValue = parseInt(value) || 0
    const currentTotal = getTotalWeight()
    const oldValue = tempWeights[key] || 0
    const newTotal = currentTotal - oldValue + numValue
    
    // Prevent going over 100
    if (newTotal > 100) {
      // Calculate maximum allowed value for this field
      const maxAllowed = 100 - (currentTotal - oldValue)
      setTempWeights(prev => ({ ...prev, [key]: maxAllowed }))
      return
    }
    
    setTempWeights(prev => ({ ...prev, [key]: numValue }))
  }

  const getTotalWeight = () => {
    return Object.values(tempWeights).reduce((sum, val) => sum + val, 0)
  }

  const saveWeights = async () => {
    const total = getTotalWeight()
    if (total !== 100) {
      alert(`Total weight must equal 100%. Current total: ${total}%`)
      return
    }
    const calculatorPayload = {}
    for (const key of CALCULATOR_WEIGHT_KEYS) {
      calculatorPayload[key] = tempWeights[key] ?? 0
    }
    let savedToVenue = false
    try {
      const res = await authFetch(getLocalApiUrl('/api/venue-config/risk-weights'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ weights: calculatorPayload }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || err.message || `Save failed (${res.status})`)
      }
      savedToVenue = true
      setWeightsSource('venue')
    } catch (e) {
      console.warn('Venue config save failed, using local fallback:', e)
      localStorage.setItem('patron_risk_weights', JSON.stringify(tempWeights))
      setWeightsSource('local')
    }
    setWeights(tempWeights)
    setShowWeightConfig(false)
    alert(
      savedToVenue
        ? 'Risk scoring weights saved to venue configuration. Patron risk recalculation (including SMR history) uses these weights.'
        : 'Weights saved locally only — could not reach venue configuration API. Patron risk still uses venue defaults until saved successfully.'
    )
  }

  const resetWeights = () => {
    setTempWeights(DEFAULT_WEIGHTS)
  }

  const resetAllWeightsInDatabase = async () => {
    if (!confirm('This will reset ALL venue weights to defaults across the entire platform. Continue?')) {
      return
    }
    
    try {
      const res = await authFetch(getLocalApiUrl('/api/venue-config/risk-weights/reset'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || err.message || `Reset failed (${res.status})`)
      }
      
      const result = await res.json()
      alert(`Successfully reset weights for all venues!\n\n${result.message || 'All venues now use normalized 100-scale defaults.'}`)
      
      // Reload configuration
      await loadConfiguration()
    } catch (e) {
      console.error('Reset failed:', e)
      alert(`Failed to reset weights: ${e.message}`)
    }
  }

  const openMatrixConfig = (matrixType) => {
    setTempMatrix(JSON.parse(JSON.stringify(riskMatrices[matrixType])))
    setShowMatrixConfig(matrixType)
  }

  const saveMatrix = () => {
    const newMatrices = { ...riskMatrices, [showMatrixConfig]: tempMatrix }
    setRiskMatrices(newMatrices)
    localStorage.setItem('patron_risk_matrices', JSON.stringify(newMatrices))
    setShowMatrixConfig(null)
    alert('Risk matrix updated successfully!')
  }

  const updateMatrixItem = (index, field, value) => {
    const updated = [...tempMatrix]
    updated[index] = { ...updated[index], [field]: parseInt(value) || value }
    setTempMatrix(updated)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Patron Risk Configuration</h1>
        <p className="text-sm text-gray-500 mt-1">
          Configure risk scoring weights and demographic risk matrices for profile risk assessment.
          {weightsSource === 'venue' && (
            <span className="block mt-1 text-psg-teal">
              Calculator weights (KYC, AML, gaming, cases, SMR, ECDD, occupation) are loaded from venue configuration.
            </span>
          )}
        </p>
        <div className="mt-3">
          <button
            onClick={resetAllWeightsInDatabase}
            className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
          >
            Reset All Venue Weights to Defaults
          </button>
          <p className="text-xs text-gray-500 mt-1">
            ⚠️ This will reset weights for ALL venues across the platform to normalized 100-scale defaults
          </p>
        </div>
      </div>

      {/* Risk Scoring Weights */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start space-x-3">
              <ShieldCheckIcon className="w-6 h-6 text-psg-teal flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Risk Scoring Weights</h3>
                <p className="text-xs text-gray-500 mt-1">
                  Maximum points each factor contributes to overall risk score
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowWeightConfig(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-psg-teal text-white rounded-lg hover:bg-psg-teal-700 transition-colors text-sm"
            >
              <CogIcon className="w-4 h-4" />
              <span>Configure</span>
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              // Compliance & Behavioral Factors
              { key: 'kyc_compliance', label: 'KYC Compliance', category: 'Compliance' },
              { key: 'aml_screening', label: 'AML/PEP Screening', category: 'Compliance' },
              { key: 'gaming_behavior', label: 'Gaming Behavior', category: 'Behavioral' },
              { key: 'case_history', label: 'Case History', category: 'Compliance' },
              { key: 'smr_history', label: 'SMR History', category: 'Regulatory' },
              { key: 'ecdd_history', label: 'ECDD History', category: 'Compliance' },
              
              // Demographic Factors
              { key: 'age', label: 'Age Risk', category: 'Demographics' },
              { key: 'gender', label: 'Gender', category: 'Demographics' },
              { key: 'nationality', label: 'Nationality/Country', category: 'Demographics' },
              { key: 'occupation_risk', label: 'Occupation Risk', category: 'Demographics' },
              { key: 'membership_duration', label: 'Membership Duration', category: 'Demographics' },
              
              // Profile Factors
              { key: 'confidence_level', label: 'ID Confidence Level', category: 'Profile' },
              { key: 'profile_age', label: 'Profile Age', category: 'Profile' },
              { key: 'activity', label: 'Activity Level', category: 'Profile' }
            ].map(({ key, label, category }) => {
              const value = weights[key] || 0
              return (
                <div key={key} className="flex justify-between items-center text-sm">
                  <div>
                    <span className="font-medium text-gray-700">{label}</span>
                    <span className="text-xs text-gray-400 ml-2">({category})</span>
                  </div>
                  <span className="font-bold text-gray-900 bg-gray-100 px-3 py-1 rounded">{value} pts</span>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Demographic Risk Matrices */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6">
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-gray-900">Demographic Risk Matrices</h3>
            <p className="text-xs text-gray-500 mt-1">
              Configure risk scores for different demographic attributes
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Age Groups */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-sm font-semibold text-gray-900">Age Groups</h4>
                <button
                  onClick={() => openMatrixConfig('age_groups')}
                  className="text-xs text-psg-teal hover:text-psg-teal-700 flex items-center gap-1"
                >
                  <PencilIcon className="w-3 h-3" /> Edit
                </button>
              </div>
              <div className="space-y-2">
                {riskMatrices.age_groups.slice(0, 3).map((item, idx) => (
                  <div key={idx} className="flex justify-between text-xs">
                    <span className="text-gray-600">{item.range}</span>
                    <span className="font-semibold text-gray-900">{item.risk_score} pts</span>
                  </div>
                ))}
                <div className="text-xs text-gray-400 italic">+{riskMatrices.age_groups.length - 3} more</div>
              </div>
            </div>

            {/* Gender */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-sm font-semibold text-gray-900">Gender Risk</h4>
                <button
                  onClick={() => openMatrixConfig('gender')}
                  className="text-xs text-psg-teal hover:text-psg-teal-700 flex items-center gap-1"
                >
                  <PencilIcon className="w-3 h-3" /> Edit
                </button>
              </div>
              <div className="space-y-2">
                {riskMatrices.gender.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-xs">
                    <span className="text-gray-600">{item.value}</span>
                    <span className="font-semibold text-gray-900">{item.risk_score} pts</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Countries */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-sm font-semibold text-gray-900">Country Risk</h4>
                <button
                  onClick={() => openMatrixConfig('countries')}
                  className="text-xs text-psg-teal hover:text-psg-teal-700 flex items-center gap-1"
                >
                  <PencilIcon className="w-3 h-3" /> Edit
                </button>
              </div>
              <div className="space-y-2">
                {riskMatrices.countries.slice(0, 3).map((item, idx) => (
                  <div key={idx} className="flex justify-between text-xs">
                    <span className="text-gray-600">{item.country}</span>
                    <span className="font-semibold text-gray-900">{item.risk_score} pts</span>
                  </div>
                ))}
                <div className="text-xs text-gray-400 italic">+{riskMatrices.countries.length - 3} more</div>
              </div>
            </div>

            {/* Occupations */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-sm font-semibold text-gray-900">Occupation Risk</h4>
                <button
                  onClick={() => openMatrixConfig('occupations')}
                  className="text-xs text-psg-teal hover:text-psg-teal-700 flex items-center gap-1"
                >
                  <PencilIcon className="w-3 h-3" /> Edit
                </button>
              </div>
              <div className="space-y-2">
                {riskMatrices.occupations.slice(0, 3).map((item, idx) => (
                  <div key={idx} className="flex justify-between text-xs">
                    <span className="text-gray-600">{item.category}</span>
                    <span className="font-semibold text-gray-900">{item.risk_score} pts</span>
                  </div>
                ))}
                <div className="text-xs text-gray-400 italic">+{riskMatrices.occupations.length - 3} more</div>
              </div>
            </div>

            {/* Membership Duration */}
            <div className="border border-gray-200 rounded-lg p-4 md:col-span-2">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-sm font-semibold text-gray-900">Membership Duration Risk</h4>
                <button
                  onClick={() => openMatrixConfig('membership_duration')}
                  className="text-xs text-psg-teal hover:text-psg-teal-700 flex items-center gap-1"
                >
                  <PencilIcon className="w-3 h-3" /> Edit
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {riskMatrices.membership_duration.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-xs">
                    <span className="text-gray-600">{item.range}</span>
                    <span className="font-semibold text-gray-900">{item.risk_score} pts</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Weight Configuration Modal */}
      {showWeightConfig && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowWeightConfig(false)} />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-7xl sm:w-full max-h-[90vh]">
              <div className="bg-white px-6 pt-5 pb-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">Configure Risk Scoring Weights</h3>
                  <button onClick={() => setShowWeightConfig(false)} className="text-gray-400 hover:text-gray-600">
                    <span className="text-2xl">&times;</span>
                  </button>
                </div>

                <div className="space-y-3">
                  <div className={`border rounded-lg p-2 ${
                    getTotalWeight() === 100 ? 'bg-green-50 border-green-200' : 
                    getTotalWeight() > 100 ? 'bg-red-50 border-red-200' : 
                    'bg-yellow-50 border-yellow-200'
                  }`}>
                    <p className={`text-xs font-medium ${
                      getTotalWeight() === 100 ? 'text-green-800' : 
                      getTotalWeight() > 100 ? 'text-red-800' : 
                      'text-yellow-800'
                    }`}>
                      <strong>{getTotalWeight() === 100 ? '✓ Perfect!' : getTotalWeight() > 100 ? '⚠ Over limit!' : 'Important:'}</strong> Total = <strong>{getTotalWeight()}/100 pts</strong> {getTotalWeight() < 100 && `(${100 - getTotalWeight()} remaining)`}
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    {/* COLUMN 1 - Compliance & Behavioral */}
                    <div className="space-y-2">
                      <div className="bg-gradient-to-r from-cyan-600 to-cyan-700 text-white px-2 py-1 rounded">
                        <h4 className="text-xs font-bold uppercase">Compliance & Behavioral</h4>
                      </div>
                      
                      {[
                        { key: 'kyc_compliance', label: 'KYC Compliance', max: 15.38 },
                        { key: 'aml_screening', label: 'AML/PEP', max: 15.38 },
                        { key: 'gaming_behavior', label: 'Gaming Behavior', max: 11.54 },
                        { key: 'case_history', label: 'Case History', max: 11.54 },
                        { key: 'smr_history', label: 'SMR History', max: 7.69 },
                        { key: 'ecdd_history', label: 'ECDD History', max: 3.85 }
                      ].map(({ key, label, max }) => (
                        <div key={key} className="bg-gray-50 p-2 rounded border border-gray-200">
                          <div className="flex justify-between items-center mb-1">
                            <label className="text-xs font-medium text-gray-700 flex-1">{label}</label>
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={tempWeights[key]}
                                onChange={(e) => handleWeightChange(key, e.target.value)}
                                className="w-12 px-1 py-0.5 text-xs border border-gray-300 rounded text-right font-semibold"
                              />
                              <span className="text-xs text-gray-400">/{max}</span>
                            </div>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={tempWeights[key]}
                            onChange={(e) => handleWeightChange(key, e.target.value)}
                            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                            style={{
                              background: `linear-gradient(to right, #0891b2 0%, #0891b2 ${tempWeights[key]}%, #e5e7eb ${tempWeights[key]}%, #e5e7eb 100%)`
                            }}
                          />
                        </div>
                      ))}
                    </div>

                    {/* COLUMN 2 - Demographics */}
                    <div className="space-y-2">
                      <div className="bg-gradient-to-r from-orange-600 to-orange-700 text-white px-2 py-1 rounded">
                        <h4 className="text-xs font-bold uppercase">Demographic Factors</h4>
                      </div>
                      
                      {[
                        { key: 'age', label: 'Age Risk', max: 3.85 },
                        { key: 'gender', label: 'Gender', max: 2.31 },
                        { key: 'nationality', label: 'Nationality/Country', max: 5.38 },
                        { key: 'occupation_risk', label: 'Occupation Risk', max: 7.69 },
                        { key: 'membership_duration', label: 'Membership Duration', max: 3.85 }
                      ].map(({ key, label, max }) => (
                        <div key={key} className="bg-gray-50 p-2 rounded border border-gray-200">
                          <div className="flex justify-between items-center mb-1">
                            <label className="text-xs font-medium text-gray-700 flex-1">{label}</label>
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={tempWeights[key]}
                                onChange={(e) => handleWeightChange(key, e.target.value)}
                                className="w-12 px-1 py-0.5 text-xs border border-gray-300 rounded text-right font-semibold"
                              />
                              <span className="text-xs text-gray-400">/{max}</span>
                            </div>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={tempWeights[key]}
                            onChange={(e) => handleWeightChange(key, e.target.value)}
                            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                            style={{
                              background: `linear-gradient(to right, #ea580c 0%, #ea580c ${tempWeights[key]}%, #e5e7eb ${tempWeights[key]}%, #e5e7eb 100%)`
                            }}
                          />
                        </div>
                      ))}
                      
                      {/* Spacer to match column 1 height */}
                      <div className="h-20"></div>
                    </div>

                    {/* COLUMN 3 - Profile Factors */}
                    <div className="space-y-2">
                      <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-2 py-1 rounded">
                        <h4 className="text-xs font-bold uppercase">Profile Factors</h4>
                      </div>
                      
                      {[
                        { key: 'confidence_level', label: 'ID Confidence Level', max: 7.69 },
                        { key: 'profile_age', label: 'Profile Age', max: 2.31 },
                        { key: 'activity', label: 'Activity Level', max: 1.54 }
                      ].map(({ key, label, max }) => (
                        <div key={key} className="bg-gray-50 p-2 rounded border border-gray-200">
                          <div className="flex justify-between items-center mb-1">
                            <label className="text-xs font-medium text-gray-700 flex-1">{label}</label>
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={tempWeights[key]}
                                onChange={(e) => handleWeightChange(key, e.target.value)}
                                className="w-12 px-1 py-0.5 text-xs border border-gray-300 rounded text-right font-semibold"
                              />
                              <span className="text-xs text-gray-400">/{max}</span>
                            </div>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={tempWeights[key]}
                            onChange={(e) => handleWeightChange(key, e.target.value)}
                            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                            style={{
                              background: `linear-gradient(to right, #9333ea 0%, #9333ea ${tempWeights[key]}%, #e5e7eb ${tempWeights[key]}%, #e5e7eb 100%)`
                            }}
                          />
                        </div>
                      ))}
                      
                      {/* Info box */}
                      <div className="mt-4 bg-blue-50 border border-blue-200 rounded p-2">
                        <p className="text-xs text-blue-800">
                          <strong>Quick Guide:</strong><br/>
                          Adjust sliders or type values. System prevents exceeding 100 pts total. Suggested max values shown as reference.
                        </p>
                      </div>
                      
                      {/* Spacer */}
                      <div className="h-12"></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-3 flex justify-between items-center border-t">
                <button
                  onClick={resetWeights}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Reset to Default
                </button>
                <div className="flex items-center space-x-3">
                  <div className={`text-sm font-semibold ${
                    getTotalWeight() === 100 ? 'text-green-600' : 
                    getTotalWeight() > 100 ? 'text-red-600' : 
                    'text-yellow-600'
                  }`}>
                    Total: {getTotalWeight()}/100 pts
                  </div>
                  <button
                    onClick={() => setShowWeightConfig(false)}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveWeights}
                    disabled={getTotalWeight() !== 100}
                    className={`px-4 py-1.5 text-sm font-medium text-white rounded-lg ${
                      getTotalWeight() === 100 ? 'bg-psg-teal hover:bg-psg-teal-700' : 'bg-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Save Weights
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Matrix Configuration Modal */}
      {showMatrixConfig && tempMatrix && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowMatrixConfig(null)} />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <div className="bg-white px-6 pt-5 pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Edit {showMatrixConfig.replace('_', ' ').toUpperCase()} Risk Matrix
                  </h3>
                  <button onClick={() => setShowMatrixConfig(null)} className="text-gray-400 hover:text-gray-600">
                    <span className="text-2xl">&times;</span>
                  </button>
                </div>

                <div className="max-h-96 overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Risk Score</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {tempMatrix.map((item, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {item.range || item.value || item.country || item.category}
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={item.risk_score}
                              onChange={(e) => updateMatrixItem(idx, 'risk_score', e.target.value)}
                              className="w-20 px-2 py-1 text-sm border border-gray-300 rounded"
                            />
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500">
                            {item.description}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                <button
                  onClick={() => setShowMatrixConfig(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={saveMatrix}
                  className="px-4 py-2 text-sm font-medium text-white bg-psg-teal rounded-lg hover:bg-psg-teal-700"
                >
                  Save Matrix
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PatronRiskConfiguration
